import { html, LitElement, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import type { FAQ, Footer, Link, SeoFAQ } from '../../../app/package/base/cms/package/cms-domain';
import { TabNavigationType } from '../../../app/package/base/router/router-types/router-types';
import { LoginStatus } from '../../../app/package/base/service/login/login-domain';
import { ModalType } from '../../../app/package/base/service/modals/modal-service';
import { LOCAL_STORAGE_KEY_A11Y_FAB_HIDDEN, REPORT_4XX__RETRY_REPORT_500 } from '../../../constants';
import { LogOutReason } from '../../../pages/logout/page';
import { FetchPriority } from '../../base/package/api/package/gaming/gaming-domain';
import { panicModeInitiated } from '../../base/package/api/package/pam/pam-api';
import { SubHelper } from '../../base/package/patterns/package/store';
import { FeatureFlag } from '../../base/package/services/package/features/feature-service';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { isDesktopWidth } from '../../base/package/util/device';
import { addLdJsonFAQ, removeLdJsonFAQ } from '../../base/package/util/html-helper';
import { localStorage_get, localStorage_set } from '../../base/package/util/localStorage';
import { UIGenericErrorModal } from '../drawers/generic-error-modal/ui-generic-error-modal';
import { UIPanicModal } from '../drawers/panic-modal/ui-panic-modal';
import styles from './ui-footer.css?inline';
import '../icons/app-store-icons/ui-apple-store-icon';
import '../icons/app-store-icons/ui-google-play-icon';
import '../icons/payment-methods/ui-apple-pay-payment-icon';
import '../icons/payment-methods/ui-google-pay-payment-icon';
import '../icons/payment-methods/ui-klarna-payment-icon';
import '../icons/payment-methods/ui-mastercard-payment-icon';
import '../icons/payment-methods/ui-paypal-payment-icon';
import '../icons/payment-methods/ui-paysafe-payment-icon';
import '../icons/payment-methods/ui-skrill-one-tap-payment-icon';
import '../icons/payment-methods/ui-skrill-payment-icon';
import '../icons/payment-methods/ui-trustly-payment-icon';
import '../icons/payment-methods/ui-visa-payment-icon';
import '../icons/ui-18-plus-icon';
import '../icons/ui-placeholder-icon';
import '../icons/ui-support-icon';
import '../ui-accordion-item/ui-accordion-item';
import '../ui-build-info/ui-build-info';
import '../ui-panic-button/ui-panic-button';

const CName = 'ui-footer';

export type SEOText = {
  base: string;
  extended: string;
};

@customElement(CName)
export class UIFooter extends LitElement {
  static readonly styles = unsafeCSS(styles);
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) isUserLoggedIn = false;
  @property({ type: Object }) footerConfig: Footer;
  @property({ type: Object }) seoText: SEOText = { base: '', extended: '' };
  @property({ type: Array }) seoFaq: SeoFAQ[];

  @state() _isCutShown = false;
  @state() _isFooterShown = false;
  @state() private _a11yFabHidden = false;
  @state() private _a11yWidgetEnabled = false;

  private readonly _showLanguageSwitcher = window.$app.appConfig.languages.length > 1;
  private readonly _showSocialLinks = false;
  private readonly _subHelper = new SubHelper();
  private _panicModeInProgress = false;

  private _handleExternalVisibilityChange = (ev: Event) => {
    const { hidden } = (ev as CustomEvent<{ hidden: boolean }>).detail;
    this._a11yFabHidden = hidden;
  };

  update(changedProperties: Map<string | number | symbol, unknown>): void {
    super.update(changedProperties);
    if (changedProperties.has('seoFaq') && this.seoFaq.length > 0) {
      // Add FAQ to the page's structured data
      const faqData: FAQ[] = this.seoFaq.map((item) => ({
        id: '',
        title: item.q,
        content: item.a,
      }));
      addLdJsonFAQ(faqData);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._a11yFabHidden = localStorage_get<boolean>(LOCAL_STORAGE_KEY_A11Y_FAB_HIDDEN, false);
    document.addEventListener('a11y-fab-visibility-changed', this._handleExternalVisibilityChange);
    window.$app.features.hasFeature(FeatureFlag.A11yWidgetEnabled).then((isEnabled) => {
      if (!this.isConnected) return;
      this._a11yWidgetEnabled = isEnabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
    document.removeEventListener('a11y-fab-visibility-changed', this._handleExternalVisibilityChange);
    removeLdJsonFAQ();
  }

  firstUpdated(): void {
    this._subHelper.addSub(
      window.$app.renderService.renderStore,
      (renderStatus) => {
        this._isFooterShown = renderStatus === 'visible';
      },
      true,
    );
  }

  render() {
    const display = this._isFooterShown ? 'block' : 'none';
    const showSocialSwitcher = this._showSocialLinks || this._showLanguageSwitcher;
    const showAppSection = !(window.$app.device.isNativeApp || window.$app.appSettings.isSHCasino);
    return html`
      <footer role="contentinfo" style="display: ${display}">
        ${this._renderPanicButton()}
        ${this._renderSeoText()}
        ${this._renderSeoFaq()}
        ${this._renderHelpCenter()}
        ${this._renderPaymentProviders()}
        ${showSocialSwitcher ? this._renderSocialSwitcherWrapper() : nothing}
        ${showAppSection ? this._renderAppSection() : nothing}
        ${this._renderResponsibleGaming()}
        ${this._renderLinks()}
        ${this._renderBuildInfo()}
      </footer>
    `;
  }

  private _renderShCrest() {
    return html`
      <img
        class="sh-crest"
        fetchpriority=${FetchPriority.LOW}
        src=${window.staticAssets.sh?.crest}
        alt="Wappen Schleswig-Holsteins"
      />
    `;
  }

  private _renderResponsibleGamingIcon() {
    return html`
      <a
        href="https://www.gluecksspiel-behoerde.de/de/erlaubnisfaehigesgluecksspiel/whitelist"
        target="_blank"
        rel="external nofollow noopener noreferrer"
      >
        <img class="responsible-gaming-logo"
          src=${window.staticAssets.games?.legal.mgaIcon}
          fetchpriority=${FetchPriority.LOW}
          alt=${window.$app.$t.get(I18n.base.responsibleGamingLogo)}
        />
      </a>
    `;
  }

  private _renderShText() {
    return html`
      <span>
        ${window.$app.$t.get(I18n.sh.footer.legalText)}
      </span>
    `;
  }

  private _renderAppSection() {
    return html`
      <ui-a11y-title level="h2">${window.$app.$t.get(I18n.a11y.heading.appStoreLinks)}</ui-a11y-title>
      <ul class="apps">
        <li>
          <a
            aria-label=${window.$app.$t.get(I18n.a11y.appLink.appleStore)}
            href=${this._getPathOpenStore('ios')}
            class="seo-link"
            tabindex="-1"
            @click=${(ev: PointerEvent) => ev.preventDefault()}
          >
            <ui-apple-store-icon @click="${() => this._openStore('ios')}"
              tabindex="0"
              role="link"
              aria-label=${window.$app.$t.get(I18n.a11y.appLink.appleStore)}
              @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._openStore.bind(this, 'ios'))}
            ></ui-apple-store-icon>
          </a>
        </li>
        <li>
          <a
            aria-label=${window.$app.$t.get(I18n.a11y.appLink.googlePlay)}
            href=${this._getPathOpenStore('android')}
            class="seo-link"
            @click=${(ev: PointerEvent) => ev.preventDefault()}
            tabindex="-1"
          >
            <ui-google-play-icon @click="${() => this._openStore('android')}"
              tabindex="0"
              role="link"
              aria-label=${window.$app.$t.get(I18n.a11y.appLink.googlePlay)}
              @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._openStore.bind(this, 'android'))}
            ></ui-google-play-icon>
          </a>
        </li>
      </ul>
    `;
  }

  private _renderBuildInfo() {
    return html`
      <ui-build-info
        commit=${window.buildInfo.commitHash}
        timeStamp=${window.buildInfo.timestamp}
        version=${window.buildInfo.version}
        .showCommit=${true}
      ></ui-build-info>
    `;
  }

  private _renderLinks() {
    return html`
      <nav class="links" aria-label="Footer">
        ${repeat(
          this.footerConfig.links,
          (link) => link.title,
          (link) => this._renderLink(link),
        )}
        ${this._renderA11yToggle()}
      </nav>
    `;
  }

  private _renderA11yToggle() {
    if (!this._a11yWidgetEnabled) return nothing;
    const label = this._a11yFabHidden ? 'Barrierefreiheit-Button einblenden' : 'Barrierefreiheit-Button ausblenden';
    return html`<button class="a11y-toggle-btn" @click=${this._toggleA11yFab}>${label}</button>`;
  }

  private _renderSocialSwitcherWrapper() {
    const wrapperClass = `social-switcher-wrapper ${isDesktopWidth() ? 'social-switcher-wrapper--desktop' : ''}`;
    return html`
      <div class=${wrapperClass}>
        ${this._showLanguageSwitcher ? this._renderUILanguageSwitcher() : nothing}
      </div>
    `;
  }

  private _renderUILanguageSwitcher() {
    import('../ui-language-switcher/ui-language-switcher');
    return html`<ui-language-switcher></ui-language-switcher>`;
  }

  private _renderSeoText() {
    if (window.$app.device.isNativeApp || window.$app.router.isGameInfoRoute() || !this.seoText.base.length) {
      return nothing;
    }

    return html`
      <div class="tipico-info">
        <ui-rich-text class="text-muted" .content="${this.seoText.base}"></ui-rich-text>
        ${this.seoText.extended.length ? this._renderExtendedSeoText() : nothing}
      </div>
    `;
  }

  private _renderExtendedSeoText() {
    return html`
      <span @click=${this._showCut} class=${this._isCutShown ? 'hidden' : 'cut'}
        tabindex="0"
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._showCut.bind(this, ev))}
      >
        ${window.$app.$t.get(I18n.base.showMore)}
      </span>
      <div class=${this._isCutShown ? 'cut-content' : 'hidden'}>
        <ui-rich-text .content="${this.seoText.extended}" class="text-muted"></ui-rich-text>
      </div>
    `;
  }

  private _renderSeoFaq() {
    if (!this.seoFaq || this.seoFaq.length === 0) {
      return nothing;
    }

    return html`
      <div>
        <h2 class="faq-title">${window.$app.$t.get(I18n.base.faq)}</h2>
        <div class="accordion" role="list">
          ${repeat(
            this.seoFaq,
            (faq) => faq.q,
            (faq) => html`
              <ui-accordion-item role="listitem">
                <div slot="title" class="accordion-title">${faq.q}</div>
                <div slot="content" class="accordion-content">
                  <ui-rich-text .content=${faq.a}></ui-rich-text>
                </div>
              </ui-accordion-item>
            `,
          )}
        </div>
      </div>
    `;
  }

  private _renderHelpCenter() {
    return html`
      <div class="help-center" role="link" tabindex="0" @click=${this._handleHelpCenterClick} @keydown=${this._handleHelpCenterKeydown}>
        <ui-a11y-title level="h2" language="en">${window.$app.$t.get(I18n.navigation.accountMenu.helpCenter)}</ui-a11y-title>
        <ui-support-icon></ui-support-icon>
        <ui-rich-text .content="${window.$app.$t.get(this.footerConfig.helpCenterText)}"></ui-rich-text>
      </div>
    `;
  }

  private _renderPaymentProviders() {
    return html`
      <ui-a11y-title level="h2">${window.$app.$t.get(I18n.a11y.heading.paymentProviders)}</ui-a11y-title>
      <ul class="payment-providers">
        ${this.footerConfig.paymentOptions.map((icon) => html`<li>${this._renderPaymentProvider(icon)}</li>`)}
      </ul>
    `;
  }

  private _renderResponsibleGaming() {
    const playerProtectionLink = window.$app.appSettings.isSHCasino
      ? 'https://casino.risk-in-safe-hands.com/'
      : 'https://games.risk-in-safe-hands.com/';

    return html`
      <div class="responsible-gaming">
        <ui-rich-text
          .content="${window.$app.$t.get(this.footerConfig.responsibleGaming.text)}"
          class="text-muted"
        ></ui-rich-text>
        <div class="responsible-gaming-icons-panel">
        ${window.$app.appSettings.isSHCasino ? this._renderShCrest() : this._renderResponsibleGamingIcon()}
          <a
            aria-label="18 Plus Logo"
            class="18-plus-logo"
            href=${playerProtectionLink}
            target="_blank"
            rel="external nofollow noopener noreferrer"
          >
            <ui-18-plus-icon></ui-18-plus-icon>
          </a>
          ${window.$app.appSettings.isSHCasino ? this._renderShText() : nothing}
        </div>
      </div>
    `;
  }

  /**
   * Example:
   *   link { id: '#footer-cookie-policy', title: 'Cookie-Einstellungen' }
   *   returns <a id="footer-cookie-policy">Cookie-Einstellungen</a>
   *   and pushed the DOM element to the GTM dataLayer as { element-footer-cookie-policy: a#footer-cookie-policy }
   * GTM scripts can add event listeners to the DOM element by using the GTM dataLayer variable.
   */
  private _renderAnchorLink(link: Link): TemplateResult {
    const id = link.nav.replace('#', '');

    const clickHandler = (ev: PointerEvent) => {
      if (id === 'footer-cookie-policy') {
        ev.preventDefault();
      }
      window.$app.track.footerNav(id);
    };

    return html`
      <a id=${id} @click=${clickHandler} href="#">
        ${window.$app.$t.get(this._getTranslationKeyForLinkTitle(link.title))}
      </a>
    `;
  }

  private _renderLink(link: Link): TemplateResult {
    if (link.nav.startsWith('#')) {
      return this._renderAnchorLink(link);
    }

    const parseHref = (url: string) => (url.startsWith('/') ? `/${window.$app.appConfig.lang}${url}` : url);
    const relAttributes = [];
    const isFollowEnabled = Boolean(link?.followEnabled);
    const isNoopener =
      link.nav.startsWith('http') && new URL(link.nav).origin !== window.$app.appConfig.canonicalOrigin;
    const shouldOpenInNewTab = link.sameSite === false;

    if (!isFollowEnabled) relAttributes.push('nofollow');
    if (isNoopener) relAttributes.push('noopener');

    return html`
      <a
        href=${parseHref(link.nav)}
        rel=${relAttributes.join(' ')}
        target=${shouldOpenInNewTab ? '_blank' : '_self'}
        @click=${(e: Event) => {
          if (!shouldOpenInNewTab) {
            e.preventDefault();
            this._handleNav(e, link.nav, shouldOpenInNewTab);
          }
        }}
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleNav.bind(this, ev, link.nav))}
      >
        ${window.$app.$t.get(this._getTranslationKeyForLinkTitle(link.title))}
      </a>
    `;
  }

  private _renderPanicButton() {
    if (this.isUserLoggedIn) {
      return html`
        <ui-a11y-title level="h2">${window.$app.$t.get(I18n.a11y.heading.panicButton)}</ui-a11y-title>
        <div class="panic-button">
          <p>${window.$app.$t.get(I18n.panic.footerText)}</p>
          <ui-panic-button
            .pressDurationS="${3}"
            .text="${window.$app.$t.get(I18n.panic.footerCTA)}"
            @panicModeOn="${this._enablePanicMode}"
          ></ui-panic-button>
        </div>
      `;
    }

    return nothing;
  }

  private _renderPaymentProvider(name: string) {
    const label = window.$app.$t.get(I18n.a11y.image.paymentMethod, { name });
    switch (name) {
      case 'PAYPAL':
        return html`<ui-paypal-payment-icon .label=${label}></ui-paypal-payment-icon>`;
      case 'SOFORT':
        return html`<ui-klarna-payment-icon .label=${label}></ui-klarna-payment-icon>`;
      case 'PAYSAFE':
        return html`<ui-paysafe-payment-icon .label=${label}></ui-paysafe-payment-icon>`;
      case 'MASTERCARD':
        return html`<ui-mastercard-payment-icon .label=${label}></ui-mastercard-payment-icon>`;
      case 'VISA':
        return html`<ui-visa-payment-icon .label=${label}></ui-visa-payment-icon>`;
      case 'TRUSTLY':
        return html`<ui-trustly-payment-icon .label=${label}></ui-trustly-payment-icon>`;
      case 'SKRILLONETAP':
        return html`<ui-skrill-one-tap-payment-icon .label=${label}></ui-skrill-one-tap-payment-icon>`;
      case 'SKRILL':
        return html`<ui-skrill-payment-icon .label=${label}></ui-skrill-payment-icon>`;
      case 'APPLEPAY':
        return html`<ui-apple-pay-payment-icon .label=${label}></ui-apple-pay-payment-icon>`;
      case 'KLARNA':
        return html`<ui-klarna-payment-icon .label=${label}></ui-klarna-payment-icon>`;
      case 'GOOGLEPAY':
        return html`<ui-google-pay-payment-icon .label=${label}></ui-google-pay-payment-icon>`;
      default:
        return html`<ui-placeholder-icon></ui-placeholder-icon>`;
    }
  }

  private _getTranslationKeyForLinkTitle(title: string) {
    const translationMap = new Map<string, string>([
      ['footer.links.aboutUs', I18n.footer.links.aboutUs],
      ['footer.links.accessibility', I18n.footer.links.accessibility],
      ['footer.links.blog', I18n.footer.links.blog],
      ['footer.links.cookieSettings', I18n.footer.links.cookieSettings],
      ['footer.links.dataProtection', I18n.footer.links.dataProtection],
      ['footer.links.gamesInfo', I18n.footer.links.gamesInfo],
      ['footer.links.imprint', I18n.footer.links.imprint],
      ['footer.links.playerProtection', I18n.footer.links.playerProtection],
      ['footer.links.sitemap', I18n.footer.links.sitemap],
      ['footer.links.termsAndConditions', I18n.footer.links.termsAndConditions],
      ['footer.links.termsOfOffer', I18n.footer.links.termsOfOffer],
    ]);

    return translationMap.get(title) ?? title;
  }

  private _handleHelpCenterClick(e: Event) {
    const path = e.composedPath?.() ?? [];
    const isAnchorClicked = path.some((n) => n instanceof HTMLAnchorElement);

    if (isAnchorClicked) return;

    window.$app.product.gotoHelpCenterOrContactUs();
  }

  private _handleHelpCenterKeydown(e: KeyboardEvent) {
    const path = e.composedPath?.() ?? [];
    const isAnchorClicked = path.some((n) => n instanceof HTMLAnchorElement);

    if (isAnchorClicked) return;

    handleKeyboardTrigger(e, this._handleHelpCenterClick.bind(this, e));
  }

  private _showCut(e: Event) {
    e.preventDefault();
    this._isCutShown = true;
  }

  private _openStore(store: 'ios' | 'android') {
    const appStoreUrl = window.$app.casinoConfig.appStores?.[store];
    if (appStoreUrl) {
      window.open(appStoreUrl, '_blank');
    }
  }

  private _getPathOpenStore(store: 'ios' | 'android') {
    const appStoreUrl = window.$app.casinoConfig.appStores?.[store];
    return appStoreUrl ?? '';
  }

  private _handleNav(ev: Event, target: string, shouldOpenInNewTab = false) {
    window.$app.track.footerNav(target);
    window.$app.logger.log(`[${CName}] handleNav: dispatch 'navigate' event:`, target);
    ev.preventDefault();
    window.$app.router.navigateTo(target, shouldOpenInNewTab ? TabNavigationType.NEW : TabNavigationType.SAME);
  }

  private _showErrorModal() {
    const modal = new UIGenericErrorModal();
    window.$app.modal.show(modal, ModalType.GenericError);
  }

  private _showPanicModeModal() {
    window.$app.loginStore.next({ loginStatus: LoginStatus.LOGGED_OUT });
    const modal = new UIPanicModal();
    modal.action = () => window.$app.router.navigateToLogout(LogOutReason.PANIC, true);
    window.$app.modal.show(modal, ModalType.PanicMode);
  }

  private async _enablePanicMode() {
    if (this._panicModeInProgress) return;
    this._panicModeInProgress = true;
    const jwt = window.$app.loginStore.value.jwt;
    if (!jwt) {
      this._panicModeInProgress = false;
      return;
    }
    await window.$app.http
      .call(window.$app.appConfig.apiUrl_pam, panicModeInitiated(jwt), REPORT_4XX__RETRY_REPORT_500)
      .then(() => this._showPanicModeModal())
      .catch(() => {
        this._panicModeInProgress = false;
        this._showErrorModal();
      });
  }

  private _toggleA11yFab() {
    this._a11yFabHidden = !this._a11yFabHidden;
    localStorage_set(LOCAL_STORAGE_KEY_A11Y_FAB_HIDDEN, this._a11yFabHidden);
    document.dispatchEvent(new CustomEvent('a11y-fab-visibility-changed', { detail: { hidden: this._a11yFabHidden } }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIFooter;
  }
}
